import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { boronClient } from "../../api";
import { StudentTagFilterInterface } from "../../domains/StudentTag";
import { createError } from "../../errors";
import { buildStudentFilterParams } from "../../helpers/FiltersHelper";
import { getNextPageParam } from "../../helpers/ReactQueryHelper";
import { paths } from "../../lib/api/v1";

const path = "/api/v1/sections/{section_id}/stays";

type SectionStaysResponse =
  paths[typeof path]["get"]["responses"]["200"]["content"]["application/json"]["stays"];
export type SectionStay = SectionStaysResponse["data"][0];

export const useFetchSectionStays = ({
  sectionId,
  query,
}: {
  sectionId: string;
  query?: StudentTagFilterInterface;
}) => {
  const result = useInfiniteQuery<SectionStaysResponse>({
    queryKey: [path, sectionId, query || {}],
    queryFn: async ({ pageParam }) => {
      const { response, data } = await boronClient.GET(path, {
        params: {
          path: {
            section_id: sectionId,
          },
          query: {
            ...(query ? buildStudentFilterParams(query) : {}),
            page: pageParam,
          },
        },
      });

      if (response.ok && data) {
        return data.stays;
      }
      throw await createError(response);
    },
    initialPageParam: 1,
    getNextPageParam,
  });

  const sectionStays = result.data?.pages?.flatMap((page) => page.data);

  return {
    ...result,
    sectionStays,
  };
};

export const useUpdateSectionStaysCache = ({
  sectionId,
}: {
  sectionId: string;
}) => {
  const queryClient = useQueryClient();

  const updateSectionStaysCache = (updatedStay: SectionStay) => {
    queryClient.setQueriesData<InfiniteData<SectionStaysResponse>>(
      { queryKey: [path, sectionId] },
      (oldData) => {
        if (!oldData) {
          return oldData;
        }

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.map((stay) =>
              stay.id === updatedStay.id ? updatedStay : stay,
            ),
          })),
        };
      },
    );
  };

  return {
    updateSectionStaysCache,
  };
};

export const useDeleteSectionStaysCache = ({
  sectionId,
}: {
  sectionId: string;
}) => {
  const queryClient = useQueryClient();

  const deleteSectionStaysCache = (stayId: string) => {
    queryClient.setQueriesData<InfiniteData<SectionStaysResponse>>(
      { queryKey: [path, sectionId] },
      (oldData) => {
        if (!oldData) {
          return oldData;
        }

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data.filter((stay) => stay.id !== stayId),
          })),
        };
      },
    );
  };

  return {
    deleteSectionStaysCache,
  };
};
